import { TellescopeModelFromName, useTellescopeMutation } from "@tellescope/tanstack-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { pendo } from "../../../../../tracking";
import {
  MessageCompositionFooter,
  MessageCompositionFooterPayload,
} from "../../../../../components/MessageCompositionFooter";
import { useDraftMutations } from "../../../hooks";
import { buildSmsMessageWithFiles } from "../../../utils";

export interface InboxSMSCompositionFooterProps {
  enduserId?: string;
  toNumber?: string;
  fromNumber?: string;
  onSend?: (sms: TellescopeModelFromName<"sms_messages">) => void;
  onSaveDraftSuccess?: () => void;
}

export const InboxSMSCompositionFooter = ({
  enduserId,
  toNumber,
  fromNumber,
  onSend,
  onSaveDraftSuccess,
}: InboxSMSCompositionFooterProps) => {
  const { t } = useTranslation("inbox");

  const sendToEnduserMutation = useTellescopeMutation({
    model: "sms_messages",
    method: "createOne",
    onSuccess: (res) => onSend?.(res),
    onError: (error) => console.error("Failed to send SMS message:", error),
  });

  const createEnduserMutation = useTellescopeMutation({
    model: "endusers",
    method: "createOne",
    onError: (error) => console.error("Failed to create enduser:", error),
  });

  const handleSend = ({ message, files }: MessageCompositionFooterPayload, sendAt?: Date) => {
    const fullMessage = buildSmsMessageWithFiles(message, files);

    if (enduserId) {
      sendToEnduserMutation.mutate({
        message: fullMessage,
        enduserId,
        enduserPhoneNumber: toNumber,
        phoneNumber: fromNumber,
        ...(sendAt && { sendAt }),
        inbound: false,
      });
    } else {
      createEnduserMutation.mutate(
        { phone: toNumber },
        {
          onSuccess: (res) =>
            sendToEnduserMutation.mutate({
              message: fullMessage,
              enduserId: res.id,
              enduserPhoneNumber: toNumber,
              phoneNumber: fromNumber,
              ...(sendAt && { sendAt }),
              inbound: false,
            }),
        }
      );
    }
  };

  const canSend = !sendToEnduserMutation.isPending && !createEnduserMutation.isPending;

  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const { saveDraft } = useDraftMutations();

  const handleSaveDraft = async ({ message, files }: MessageCompositionFooterPayload) => {
    if (!toNumber) return;
    setIsSavingDraft(true);
    const resolvedEnduserId = enduserId || (await createEnduserMutation.mutateAsync({ phone: toNumber }))?.id;
    if (!resolvedEnduserId) {
      setIsSavingDraft(false);
      return;
    }

    await saveDraft({
      message: buildSmsMessageWithFiles(message, files),
      fields: {
        channel: "SMS",
        enduserId: resolvedEnduserId,
        enduserPhoneNumber: toNumber,
        phoneNumber: fromNumber,
        inbound: false,
      },
    });
    setIsSavingDraft(false);
    onSaveDraftSuccess?.();
  };

  return (
    <MessageCompositionFooter
      placeholder={t("Type your message...")}
      canSend={canSend}
      enduserId={enduserId}
      channelType="sms"
      onSubmit={(payload) => {
        pendo.track("new-sms-thread-send");
        handleSend(payload);
      }}
      onSchedule={(payload, sendAt) => handleSend(payload, sendAt)}
      onSaveDraft={toNumber ? handleSaveDraft : undefined}
      isSavingDraft={isSavingDraft}
      trackingIds={{ schedule: "sms-compose-schedule-message-dialog-open" }}
    />
  );
};
